import DynamicSectionElement from '@/js/dynamic-section-element';
import {
  dispatchCartClickEventListener,
  removeVariantsRequest,
  onCartUpdate
} from '@/js/utils/cart-utilities.js';

class FlyoutCart extends DynamicSectionElement {
  constructor() {
    super();
    this.buttonCheckout = undefined;
    this.closeOnClickOutside = this.closeOnClickOutside.bind(this);
  }

  connectedCallback() {
    this.onLoadEventListeners();
    this.initVariables();
    this.setEventListeners();
    this.removeSagawaFeesFromCart();
  }

  /**
   * Open the flyout cart if `?cart` param is present in the URL and remove param after.
   * @returns {void}
   */
  handleRedirectFromCart() {
    const url = new URL(globalThis.location);
    const parameters = url.searchParams;
    if (parameters.has('cart')) {
      this.openFlyoutCart();
      parameters.delete('cart');
      globalThis.history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Initialize variables for the flyout cart
   * @returns {void}
   */
  initVariables() {
    this.closeCarts = this.querySelectorAll('[data-close-cart]');
    this.buttonCheckout = this.querySelector('[data-btn-checkout]');
    this.sectionsArray = [this.sectionId];
  }

  /**
   * Set event listeners for the flyout cart once
   * @returns {void}
   */
  onLoadEventListeners() {
    this.handleRedirectFromCart();

    this.autoOpenCart = Object.hasOwn(this.dataset, 'autoOpenCart');

    if (this.autoOpenCart) {
      // Open flyout cart when custom event is fired
      document.addEventListener('pxl:addToCartSuccess', () =>
        this.openFlyoutCart()
      );
    }

    // Toggle the flyout cart
    document.addEventListener('pxl:flyoutCart:toggle', () =>
      this.toggleFlyoutCart(!this.closest('section').hasAttribute('open'))
    );
  }

  /**
   * Set event listeners for the flyout cart
   * @returns {void}
   */
  setEventListeners() {
    if (this.closeCarts.length <= 0) return;

    for (let closeCart of this.closeCarts) {
      dispatchCartClickEventListener(closeCart);
    }

    this.closeCartOnEscape();

    // Add an event listener to load the cart when the cart icon is clicked
    if (!this.buttonCheckout) return;
    this.buttonCheckout.addEventListener('click', () => {
      this.buttonCheckout.classList.add('btn-loading');
    });
  }

  /**
   * Close the flyout cart
   * @returns {void}
   */
  closeFlyoutCart() {
    this.toggleFlyoutCart(false);
  }

  /**
   * Open the flyout cart
   * @returns {void}
   */
  openFlyoutCart() {
    this.toggleFlyoutCart(true);
  }

  /**
   * Handle the flyout cart open/close state and add classes to the body
   * @param {boolean} open - The state to set the flyout cart
   * @returns {void}
   */
  toggleFlyoutCart(open) {
    const cartDrawer = this.closest('section');
    const body = document.body;

    // Adds the overlay
    body.toggleAttribute('cart-open', open);
    cartDrawer.toggleAttribute('open', open);

    // Broadcast a custom event to let other components know that the cart is open or closed
    document.dispatchEvent(
      new CustomEvent('pxl:flyoutCart:toggleState', {
        bubbles: true,
        detail: { open: open }
      })
    );

    // If the cart is open, we add an event listener to close it when we click outside of it. But when we close it, we remove this.
    // Also, to avoid a collision with the click that triggered this, we add a miniscule timeout before adding the event listener.
    open
      ? setTimeout(() => {
          document.addEventListener('click', this.closeOnClickOutside);
        }, 1)
      : document.removeEventListener('click', this.closeOnClickOutside);
  }

  /**
   * Method to close the flyout cart under certain conditions based on passed event
   * @param {Event} event - The event object
   * @returns {void}
   */
  closeOnClickOutside(event) {
    const flyoutCart = this.closest('[data-cart-drawer]');
    const cartIcon = document.querySelector('cart-icon');
    // Check if the click event's target is outside of the flyout cart and is not the cart icon
    if (
      !flyoutCart.contains(event.target) &&
      !cartIcon.contains(event.target)
    ) {
      this.closeFlyoutCart();
      document.body.classList.remove('no-scroll');
    }
  }

  /**
   * Close the cart when the user presses the escape key
   * @returns {void}
   */
  closeCartOnEscape() {
    document.addEventListener('keyup', event => {
      if (!document.body.hasAttribute('cart-open')) return;
      if (event.key === 'Escape') {
        this.closeFlyoutCart();
      }
    });
  }

  afterSectionRender() {
    this.initVariables();
    this.setEventListeners();
    this.removeSagawaFeesFromCart();
  }

  /**
   * Async method to remove the Sagawa fee from the cart that may have been applied during checkout
   * @returns {void}
   */
  removeSagawaFeesFromCart = async () => {
    try {
      const response = await fetch(globalThis.Shopify.routes.root + 'cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        cache: 'no-store'
      });

      this.cart = await response.json();

      for (const item of this.cart.items) {
        // if sku is null go to next item
        if (item.sku === null) continue;

        if (item.sku.startsWith('SGW-COD')) {
          const response = await removeVariantsRequest(
            [item.variant_id],
            this.sectionsArray
          );
          const jsonResponse = await response.json();
          onCartUpdate(jsonResponse);
        }
      }
    } catch (error) {
      /* eslint-disable no-console */
      console.error('Error removing Sagawa fees:', error);
    }
  };
}

customElements.get('flyout-cart') ||
  customElements.define('flyout-cart', FlyoutCart);
